import { ArrowRight, FolderPlus, Lightbulb, Mail, Newspaper, Search, Send, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { ConfirmDialog, Modal, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Textarea,
} from '../components/ui/primitives';
import type { ProjectKind } from '../types/api';
import { useCreateProject, useDeleteProject, useIsAdmin, useProjects } from '../hooks/queries';
import { formatNumber, formatRelative } from '../lib/utils';

const EXAMPLE_CONTEXT = `What we sell, who it is for, and why a partner would care.
Include the numbers you are happy to quote in an email.`;

export const ProjectsPage = () => {
  const { data: projects, isLoading, error } = useProjects();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const createProject = useCreateProject();
  const deleteProject = useDeleteProject();
  const toast = useToast();

  const creating = searchParams.get('new') === '1';
  const setCreating = (open: boolean) => {
    const params = new URLSearchParams(searchParams);
    open ? params.set('new', '1') : params.delete('new');
    setSearchParams(params, { replace: true });
  };
  const filteredProjects = (projects ?? []).filter((project) =>
    (!kindFilter || project.kind === kindFilter) &&
    `${project.name} ${project.description ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [form, setForm] = useState<{
    name: string;
    kind: ProjectKind;
    description: string;
    websiteUrl: string;
    productContext: string;
  }>({ name: '', kind: 'OUTREACH', description: '', websiteUrl: '', productContext: '' });

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('A project needs a name');
      return;
    }
    try {
      const project = await createProject.mutateAsync({
        name: form.name.trim(),
        kind: form.kind,
        description: form.description || undefined,
        websiteUrl: form.websiteUrl || undefined,
        productContext: form.productContext || undefined,
      });
      toast.success('Project created — add your context to get started');
      setCreating(false);
      setForm({ name: '', kind: 'OUTREACH', description: '', websiteUrl: '', productContext: '' });
      navigate(`/projects/${project.id}`);
    } catch (error) {
      toast.error(error);
    }
  };

  if (isLoading) return <LoadingBlock />;
  if (error) return <Page><ErrorBlock error={error} /></Page>;

  return (
    <Page>
      <PageHeader
        title="Projects"
        description="A home for each outreach or content initiative. Choose a project to get to work."
        actions={
          <Button
            variant="primary"
            icon={<FolderPlus className="h-4 w-4" />}
            onClick={() => setCreating(true)}
          >
            New project
          </Button>
        }
      />

      {Boolean(projects?.length) && <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-faint" />
          <Input aria-label="Search projects" placeholder="Search projects…" value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" />
        </div>
        <Select aria-label="Filter project type" className="w-auto" value={kindFilter} onChange={(event) => setKindFilter(event.target.value)}>
          <option value="">All project types</option><option value="OUTREACH">Outreach</option><option value="CONTENT">Content</option>
        </Select>
        <span className="text-xs text-muted">{filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}</span>
      </div>}
      {projects?.length && !filteredProjects.length ? <Card><EmptyState title="No matching projects" description="Try a different name or project type." action={<Button onClick={() => { setSearch(''); setKindFilter(''); }}>Clear filters</Button>} /></Card> : projects?.length ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filteredProjects.map((project) => (
            <Card key={project.id} className="flex flex-col p-5 transition-colors hover:border-faint/60">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/projects/${project.id}`} className="min-w-0 flex-1">
                  <h2 className="truncate text-[15px] font-semibold text-ink hover:text-brand">
                    {project.name}
                  </h2>
                  <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-muted">
                    {project.description ?? 'No description yet.'}
                  </p>
                </Link>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Badge tone="brand">{project.kind === 'CONTENT' ? 'Content' : 'Outreach'}</Badge>
                  <Badge tone={project.status === 'ACTIVE' ? 'positive' : 'neutral'}>
                    {project.status.toLowerCase()}
                  </Badge>
                </div>
              </div>

              {project.kind === 'CONTENT' ? (
                <div className="mt-4 grid grid-cols-2 gap-2 border-t border-line pt-3 text-center">
                  <div>
                    <div className="flex items-center justify-center gap-1 text-xs text-faint">
                      <Lightbulb className="h-3 w-3" /> Topics
                    </div>
                    <div className="mt-0.5 text-base font-semibold tabular-nums">
                      {formatNumber(project.stats?.topics ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-1 text-xs text-faint">
                      <Newspaper className="h-3 w-3" /> Posts
                    </div>
                    <div className="mt-0.5 text-base font-semibold tabular-nums">
                      {formatNumber(project.stats?.posts ?? 0)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-line pt-3 text-center">
                  <div>
                    <div className="flex items-center justify-center gap-1 text-xs text-faint">
                      <Users className="h-3 w-3" /> Leads
                    </div>
                    <div className="mt-0.5 text-base font-semibold tabular-nums">
                      {formatNumber(project.stats?.leads ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-1 text-xs text-faint">
                      <Send className="h-3 w-3" /> Sent
                    </div>
                    <div className="mt-0.5 text-base font-semibold tabular-nums">
                      {formatNumber(project.stats?.emailsSent ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-1 text-xs text-faint">
                      <Mail className="h-3 w-3" /> Replies
                    </div>
                    <div className="mt-0.5 text-base font-semibold tabular-nums">
                      {formatNumber(project.stats?.repliesReceived ?? 0)}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-auto flex items-center justify-between gap-2 pt-5">
                <span className="text-2xs text-faint">
                  Updated {formatRelative(project.updatedAt)}
                </span>
                <div className="flex items-center gap-1">
                  <Link to={`/projects/${project.id}`} className="inline-flex min-h-9 items-center gap-2 rounded-lg px-3 text-xs font-medium text-brand hover:bg-brand/5">
                    Open project <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  {isAdmin && <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${project.name}`}
                    onClick={() => setDeleteTarget({ id: project.id, name: project.name })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<FolderPlus className="h-5 w-5" />}
            title="No projects yet"
            description="Create an outreach project to find and contact leads, or a content project to plan and publish posts."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                Create the first project
              </Button>
            }
          />
        </Card>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New project"
        description="You can fill in the prompts later, on the project overview."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={createProject.isPending}>
              Create project
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label="Project type"
            htmlFor="project-kind"
            hint="Outreach runs leads, campaigns and email. Content runs the blog: topics, posts and publishing. This can be changed later."
          >
            <Select
              id="project-kind"
              value={form.kind}
              onChange={(event) => setForm({ ...form, kind: event.target.value as ProjectKind })}
            >
              <option value="OUTREACH">Outreach (GTM) — leads &amp; email</option>
              <option value="CONTENT">Content — blog posts</option>
            </Select>
          </Field>
          <Field label="Name" htmlFor="project-name">
            <Input
              id="project-name"
              autoFocus
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder={form.kind === 'CONTENT' ? 'RentX blog' : 'RentX go-to-market'}
            />
          </Field>
          <Field label="Description" htmlFor="project-description" hint="One line for your own reference.">
            <Input
              id="project-description"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              placeholder="Partner and affiliate acquisition"
            />
          </Field>
          <Field label="Website" htmlFor="project-website">
            <Input
              id="project-website"
              value={form.websiteUrl}
              onChange={(event) => setForm({ ...form, websiteUrl: event.target.value })}
              placeholder="https://rentx.example"
            />
          </Field>
          <Field
            label="Product context"
            htmlFor="project-context"
            hint="Given to every agent verbatim. The more concrete, the better the emails."
          >
            <Textarea
              id="project-context"
              rows={6}
              className="prompt-input"
              value={form.productContext}
              onChange={(event) => setForm({ ...form, productContext: event.target.value })}
              placeholder={EXAMPLE_CONTEXT}
            />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete project"
        destructive
        confirmLabel="Delete everything"
        loading={deleteProject.isPending}
        message={
          <>
            Deleting <strong className="text-ink">{deleteTarget?.name}</strong> also removes its lead
            types, leads, campaigns, emails and jobs. This cannot be undone.
          </>
        }
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteProject.mutateAsync(deleteTarget.id);
            toast.success('Project deleted');
            setDeleteTarget(null);
          } catch (error) {
            toast.error(error);
          }
        }}
      />
    </Page>
  );
};
